import { Header, Content, InlineHeader, InlineSection, HeaderRenamePillButton } from '@/components/DashboardComponents'
import { Button } from '@/form/FormComponents'
import { useNavigate, useRouteLoaderData, useLoaderData, useRevalidator, NavLink } from 'react-router'
import { GenericEmptyListPlaceholder } from '@/DashboardLayout'
import { Modal, ModalContent, ModalButtons, useModal, useErrorModal } from '@/components/Modal'
import { useMenu } from '@/components/Menu'
import { useUser } from '@/systems/Authentication'
import { publicAction } from '@/data/loaders'
import { useShareModal } from '@/components/FilesystemShareModals';
import { useMove, useTrash, useRenameScenarioModal, useAssignScenarioModal, useCopyScenarioModal, useSharedWithMe, useHandleRestore } from '@/pages/Filesystem';
import { List, ListItem, ItemColumn } from '@/components/List';
import { shortDate, formatDate, getDocumentationTypeName } from '@/shared/utils';
import { ShareButton } from '@/components/ShareButton';
import { Icon } from '@/components/Icon';
import { useBreakpoint } from '@/hooks/useIsMobile';

export function ScenarioOverview() {
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const { file, scenario, preview_sessions, rubrics } = useLoaderData().data.value;
  const courses = useRouteLoaderData("dashboard_root").data.value.filter(c => !c.archived);
  const { uid } = useUser();

  const [openAssignScenarioModal] = useAssignScenarioModal({ courses });

  const [handleShare] = useShareModal();
  const { moveMenuItem } = useMove({ file, folder: file.folder, view: "scenarios" });
  const { trashMenuItem } = useTrash({ file, folder: file.folder, view: "scenarios", dont_show_remove_option: true, go_back_upon_trash: true });
  const [openRenameScenarioModal] = useRenameScenarioModal();
  const [openCopyScenarioModal] = useCopyScenarioModal();

  const { addToMyMenuItem, openAddToMyModal } = useSharedWithMe({ file, is_shared: file._is_directly_shared });
  const show_add_button = useBreakpoint((width) => width > 700);

  function handleCopyScenario() {
    openCopyScenarioModal({ file, show_choose_location: true, append_copy_to_title: ["edit", "owner"].includes(file._level), callback: ({ item_id }) => {
      navigate(`/scenarios/${item_id}`);
    } });
  }

  const handleRename = () => {
    openRenameScenarioModal({
      file,
      callback: () => {
        revalidator.revalidate();
      }
    })
  }

  const menuItems = [
    ...addToMyMenuItem,
    {
      label: 'Rename',
      icon: 'rename-outline',
      onClick: handleRename,
      disabled: !["edit", "owner"].includes(file._level),
    },
    {
      label: 'Make a copy',
      icon: 'file-copy',
      onClick: handleCopyScenario,
    },
    "separator",
    {
      label: 'Share',
      icon: 'account-plus-outline',
      onClick: () => {handleShare(file)},
      disabled: !["edit", "owner"].includes(file._level),
    },
    ...moveMenuItem,
    ...(trashMenuItem.length > 0 ? ["separator"] : []),
    ...trashMenuItem
  ]

  const assigned_to_course_ids = [...new Set((file.linked_assessments || []).filter(a => a.in_sync != null ? a.in_sync : true).map(a => a.course_id).reverse())].reverse(); // Remove the duplicates, keeping the latest index of each instead of the earliest index
  const assigned_to_courses = assigned_to_course_ids.map(course_id => {
    return {
      name: courses.find(course => course._id == course_id)?.name,
      id: course_id,
    }
  }).filter(course => course.name).slice(0, 3)
  const extra_courses_count = assigned_to_course_ids.length-assigned_to_courses.length;

  const copy_banner = file._added_copy_item_id && file._is_directly_shared && (
    <div className="callout_hint_box">
      <div>
        <div><Icon name="file-copy"/></div>
        <div className="links">You made your own copy of this scenario. <a onClick={() => {navigate(`/scenarios/${file._added_copy_item_id}`)}}>Open my copy</a></div>
      </div>
    </div>
  )

  const { handleRestore, is_restoring } = useHandleRestore({ file });

  const trash_banner = file.trashed && (
    <div className="callout_hint_box">
      <div>
        <div><Icon name="trash-can-outline"/></div>
        <div>This scenario is in the trash and will be permanently deleted soon.</div>
        {(file.trashed_head && file.uid === uid) && <div className="callout_small_button">
          <Button text="Restore" icon="history" className="hollow small" onClick={handleRestore} loading={is_restoring}/>
        </div>}
      </div>
    </div>
  )

  const mobile_button_sizes = useBreakpoint((width) => width < 400);

  return (
    <>
      <Header title={<HeaderRenamePillButton text={file.title} onClick={handleRename} disabled={!["edit", "owner"].includes(file._level)} />} backButton={true} menuItems={menuItems}>
        {show_add_button && file._is_directly_shared && !(file._added_shortcut_id || file._added_copy_item_id) && <Button icon={"forum-plus-outline"} text={`Add to my scenarios`} onClick={openAddToMyModal} />}
        {!mobile_button_sizes && <ShareButton file={file} />}     
      </Header>
      <Content>
        {copy_banner}
        {trash_banner}
        <div className="scenario_overview_container">
          <div>
            <InlineHeader title="Scenario overview">
              {["edit", "owner"].includes(file._level) ? (
                <Button icon="pencil-outline" className="hollow" backgroundColor="var(--header)" text={mobile_button_sizes ? "Edit" : "Edit scenario"} onClick={() => {navigate(`/scenarios/${file.item_id}/edit`)}} disabled={!["edit", "owner"].includes(file._level) || file.trashed}/>
              ) : (
                <Button icon="copy-and-edit" className="hollow" backgroundColor="var(--header)" text="Make a copy to edit" onClick={handleCopyScenario}/>
              )}              
            </InlineHeader>
            <InlineSection>
              <div className="text_section">
                <div>Opening line</div>
                <div className="text">{scenario.data.opening_blurb}</div>
              </div>
              {!(scenario.data.diagnosis_and_management && !scenario.data.new_diagnosis) && scenario.data.diagnoses.length > 0 && (
                <div className="text_section">
                  <div>{scenario.data.diagnoses.length > 1 ? "New diagnoses" : "New diagnosis"}</div>
                  <div className="text">{scenario.data.diagnoses.map(d => d.diagnosis).join(", ")}</div>
                </div>
              )}
              <div className="text_section">
                <div>Objectives</div>
                {((pills) => pills.length > 0
                  ? <div className="pills">{pills.map((label, i) => <div key={i} className="pill"><div><span>{label}</span></div></div>)}</div>
                  : <div className="text">None</div>
                )([
                  scenario.data.diagnosis_and_management && scenario.data.new_diagnosis && 'New diagnosis',
                  scenario.data.diagnosis_and_management && scenario.data.management_of_known_diagnosis && 'Management of known diagnosis',
                  scenario.data.diagnosis_and_management && scenario.data.new_diagnosis && scenario.data.new_diagnosis_and_differentials && scenario.data.capture_differentials && 'Maintain a list of differentials',
                  (scenario.data.diagnosis_and_management && scenario.data.write_documentation) && `Write ${getDocumentationTypeName(scenario.data)}`,
                  scenario.data.conversation_based_objective && 'Specific patient conversation',
                  (scenario.data.conversation_based_objective && scenario.data.write_conversation_summary && !(scenario.data.diagnosis_and_management && scenario.data.write_documentation)) && `Write conversation summary`,
                ].filter(Boolean))}
              </div>
              <div className="text_section">
                <div>Features</div>
                <div className="pills">
                  {scenario.data.conversation_mode === "text" || (scenario.data.conversation_mode === "voice" && scenario.data.allow_text_mode) && <div className="pill"><div><Icon name="comment-text-outline-small"/><span>Typing</span></div></div>}
                  {scenario.data.conversation_mode === "voice" && <div className="pill"><div><Icon name="microphone"/><span>Speaking</span></div></div>}
                  {scenario.data.diagnosis_and_management && scenario.data.encounter_stages !== "subjective" && scenario.data.allow_physical_exam && <div className="pill"><div><Icon name="human-search"/><span>Physical exam</span></div></div>}
                  {scenario.data.diagnosis_and_management && scenario.data.encounter_stages !== "subjective" && scenario.data.allow_diagnostics && <div className="pill"><div><Icon name="test-tube"/><span>Diagnostics</span></div></div>}
                  {scenario.data.diagnosis_and_management && scenario.data.allow_interventions && <div className="pill"><div><Icon name="needle"/><span>Interventions</span></div></div>}
                  {scenario.data.diagnosis_and_management && scenario.data.consult_for_advice && <div className="pill"><div><Icon name="doctor"/><span>Consult for advice</span></div></div>}
                </div>
              </div>
              <div className="text_section">
                <div>Included rubrics</div>
                {rubrics.length > 0 ? (
                  <div className="pills">
                    {rubrics.map((r, index) => {
                      const contents = <div>
                        <Icon name={(r._id || !r.title) ? (r._id?.startsWith("default") ? "sapient logo" : "bullseye-arrow") : "bullseye-arrow-edit"} style={{fill: r._id ? (r._id.startsWith("default") ? "" : "var(--rubric_file_color)") : ""}}/><span>{r.title ? r.title : <i>{"Rubric just for this scenario"}</i>}</span>
                      </div>
                      if (r._has_access && !r._id?.startsWith("default")) {
                        return <NavLink to={`/rubrics/${r._id}`} key={r._id || index} className="pill clickable">{contents}</NavLink>;
                      } else {
                        return <NavLink to={`/scenarios/${file.item_id}/edit#rubrics`} key={r._id || index} className="pill clickable">{contents}</NavLink>;
                      }
                    })}
                  </div>
                ) : (
                  <div className="text">None</div>
                )}
              </div>
            </InlineSection>
          </div>
          <div>
            <div>
              <InlineHeader title="Previews">
                <Button icon="play-outline" className="hollow" backgroundColor="var(--header)" text="Preview" onClick={() => {navigate(`/scenarios/${file.item_id}/preview`)}}/>
              </InlineHeader>
              <InlineSection halfMarginTop={true}>
                {preview_sessions.length > 0 ? (
                  <>
                    <List columns={[{ name: "Date" }, { name: "Person", width: 123 }]} hide_header={true} menu={true} compact={true}>
                      {preview_sessions.slice(0, 5).map(session => <PreviewSessionItem key={session._id} session={session} scenario_version_id={scenario.version_id} is_editor={["edit", "owner"].includes(file._level)} />)}
                    </List>
                    {preview_sessions.length > 5 && <div className="extra_courses_count links"><a onClick={() => {navigate(`/scenarios/${file.item_id}/previews`)}} tabIndex={0}>See all previews</a></div>}
                  </>
                ) : <GenericEmptyListPlaceholder text="No previews yet"/>}
              </InlineSection>
            </div>
            <div>
              <InlineHeader title="Assigned to">
                <Button icon="assignment-plus" className="hollow" backgroundColor="var(--header)" text="Assign" onClick={() => {openAssignScenarioModal({ assign_scenario_id: file.item_id })}} disabled={file.trashed}/>
              </InlineHeader>
              <InlineSection halfMarginTop={true}>
                {assigned_to_course_ids.length > 0 ? (
                  <>
                    {assigned_to_courses.length > 0 ? <>
                      <List columns={[{ name: "Title" }]} hide_header={true} menu={false} compact={true}>
                        {assigned_to_courses.map(c => <ListItem key={c.id} icon={"book-open-variant-outline"} onClick={() => {navigate(`/courses/${c.id}`)}}>
                          <ItemColumn>{c.name}</ItemColumn>
                        </ListItem>)}
                      </List>
                      {extra_courses_count > 0 && <div className="extra_courses_count">{`...and ${extra_courses_count} other course${extra_courses_count > 1 ? "s" : ""}`}</div>}
                    </> : (
                      <GenericEmptyListPlaceholder text={`${extra_courses_count} course${extra_courses_count > 1 ? "s" : ""} you aren't a member of`}/>
                    )}
                  </>
                ) : <GenericEmptyListPlaceholder text="Not assigned to any courses yet"/>}
              </InlineSection>
            </div>
          </div>
        </div>
      </Content>
    </>
  )
}

export function PreviewSessionItem({ session, scenario_version_id, assessment_scenario_version_id, full_size = false, is_editor = false, is_assessment = false }) {
  const navigate = useNavigate();
  const { uid } = useUser();

  const revalidator = useRevalidator();
  const [openErrorModal] = useErrorModal();

  const [openRemoveModal] = useModal(function ({ closeModal, setLoading }) {
    function submitRemovePreview() {
      setLoading(true);
  
      publicAction("makeSessionPrivate", { session_id: session._id }).then(function(data) {
        closeModal();
        revalidator.revalidate();
      }).catch(function(error) {
        console.error(error);
        closeModal();
        openErrorModal();
      });
    }

    return (
      <Modal title="Remove preview?">
        <ModalContent>
          {`This preview will be removed from the list and made private, but not deleted.`}
        </ModalContent>
        <ModalButtons>
          <Button text="Cancel" className="hollow" onClick={closeModal}/>
          <Button text="Remove" className="destructive" onClick={submitRemovePreview}/>
        </ModalButtons>
      </Modal>
    )
  });

  const menu = useMenu({
    menuItems: [
      {
        icon: 'link',
        label: 'Copy link',
        onClick: () => {navigator.clipboard.writeText(`https://app.sapientclinician.com/session/${session._id}/review`)},
      },
      "separator",
      {
        label: 'Remove from list',
        icon: 'close',
        onClick: openRemoveModal,
        disabled: !(is_editor || session.uid === uid),
      }
    ]
  });

  const is_review = session.submitted || session.uid !== uid || !((session.scenario_version_id && session.scenario_version_id === scenario_version_id) || (session.assessment_scenario_version_id && session.assessment_scenario_version_id === assessment_scenario_version_id));

  return (
    <ListItem icon={session.returned ? "check-circle" : (session.submitted ? "check-circle-outline" : "progress-clock")} iconColor={session.submitted ? "var(--submission_graded)" : "var(--submission_in_progress)"} onClick={() => {navigate(`/session/${session._id}${is_review ? "/review" : ""}`)}} menu={menu}>
      <ItemColumn>{full_size ? formatDate(session.date) : shortDate(session.date)}</ItemColumn>
      <ItemColumn className="item_shared_by">{full_size && <Icon name="account-circle-outline" className="shared_by_icon"/>}<span>{(session.user._is_same_org || session.user._is_you || is_assessment) ? ((session.user.name + (session.user._is_you ? " (you)" : "")) || "(Deleted user)") : session.user.email}</span></ItemColumn>
    </ListItem>
  )
}

export function ScenarioPreviews() {
  const { file, preview_sessions, scenario_version_id } = useLoaderData().data.value;
  const navigate = useNavigate();

  return (
    <>
      <Header title={`Previews of "${file.title}"`} backButton={true}>
        <Button icon="play-outline" text="Preview" className="hollow" backgroundColor="var(--header)" onClick={() => {navigate(`/scenarios/${file.item_id}/preview`)}}/>
      </Header>
      <div className="dashboard_content_inner nearest_scrollable_container">
        <List fullPage={true} columns={[{ name: "Date", min_width: 200 }, { name: "Previewed by", width: 260, hide_order: 1 }]} menu={true}>
          {preview_sessions.map(session => <PreviewSessionItem key={session._id} session={session} scenario_version_id={scenario_version_id} full_size={true} is_editor={["edit", "owner"].includes(file._level)} />)}
        </List>
        {preview_sessions.length === 0 && <GenericEmptyListPlaceholder inline text="No previews yet"/>}
      </div>
    </>
  )
}